import { useMemo } from "react";
import { Menu } from "@progress/kendo-react-layout";
import { connect } from "react-redux";

import { IconType } from "components/Icon/Icon";
import { WindowId } from "constants/tee";
import { useNewEncounter } from "contexts/NewEncounterContext";
import { useFeatureFlags } from "hooks/useFeatureFlags";
import { UserRole } from "models";
import { STORAGE_TYPE } from "scenes/SystemPreferences/components/SecuritySettings";
import { canCreateEncounters } from "utils/user";

import { createLinkRender } from "./createLinkRender";
import { useResetMenuOnLeafSelect } from "./useResetMenuOnLeafSelect";

import type React from "react";
import type { To } from "react-router";
import type { IconTypeValue } from "components/Icon/Icon";
import type { UserState } from "models";
import type { State } from "reducers";
import type { SystemPreferencesState } from "scenes/SystemPreferences/models/systemPreferencesState";

interface MenuItemData {
  btnClass?: string;
  onClick?: () => void;
  openInNewTab?: boolean;
  icon?: IconTypeValue;
  singleInstanceId?: WindowId;
  separator?: boolean;
}

interface MenuListType {
  user: UserState;
  hasChangesCallback?: (to: To, clickHandler?: () => void) => void;
  isErrorMode: boolean;
  encounterOpened: boolean;
  systemPreferences: SystemPreferencesState;
}

interface MenuItem {
  text?: string;
  url?: string;
  disabled?: boolean;
  data?: MenuItemData;
  items?: MenuItem[];
  cssClass?: string;
}

const MainMenuList: React.FC<MenuListType> = ({
  user,
  hasChangesCallback,
  isErrorMode,
  encounterOpened,
  systemPreferences,
}) => {
  const { openNewEncounterDialog } = useNewEncounter();
  const { hasFAC, hasPSA, hasEither } = useFeatureFlags();
  const { menuKey, onSelect } = useResetMenuOnLeafSelect();

  const isCreateEncounterDisabled =
    !canCreateEncounters(user.allAvailablePermissions) && !user.isSystemAdmin;

  const linkRender = useMemo(
    () =>
      createLinkRender({
        hasChangesCallback,
        isErrorMode,
        encounterOpened,
      }),
    [hasChangesCallback, isErrorMode, encounterOpened],
  );

  if (user.loading || !user || !hasEither) {
    return <Menu />;
  }

  const menuList: MenuItem[] = [
    {
      text: "Patient Encounters",
      url: "/patient-encounters",
      disabled: !user.privileges.canSearch,
      data: { btnClass: "patient-encounters-button" },
    },
    {
      text: "Add New...",
      disabled: isCreateEncounterDisabled,
      data: {
        btnClass: "add-new-button",
        onClick: openNewEncounterDialog,
      },
    },
    {
      data: { separator: true },
      cssClass: "ts-menu-separator",
    },
    {
      text: "Tools",
      items: [
        {
          text: "CodeBooks",
          url: "/codebooks",
          data: {
            icon: IconType.BookCodebooks,
            singleInstanceId: WindowId.CodeBooks,
          },
        },
        {
          text: "References",
          url: "/references",
          data: {
            icon: IconType.BookReferences,
            singleInstanceId: WindowId.References,
          },
        },
      ],
    },
  ];

  if (
    (user.isSystemAdmin || user.highestRole >= UserRole.POWER) &&
    systemPreferences?.generalPreferences?.SessionStorageType !== STORAGE_TYPE.InMemory
  ) {
    const reportData = { icon: IconType.FilePlus, openInNewTab: true };

    menuList.push({
      text: "Reports",
      items: [
        ...(hasFAC ?
          [
            { text: "DRG Change", url: "/reports/drg-change", data: reportData },
            { text: "DRG List", url: "/reports/drg-list", data: reportData },
            { text: "APC Change", url: "/reports/apc-change", data: reportData },
          ]
        : []),
        ...(hasPSA ?
          [
            {
              text: "Unlisted Codes",
              url: "/reports/unlisted-codes",
              data: reportData,
              disabled: true,
            },
          ]
        : []),
        {
          text: "Record Status Detail",
          url: "/reports/record-status-detail",
          data: reportData,
        },
        {
          text: "Coder Productivity List",
          url: "/reports/coder-productivity-list",
          data: reportData,
        },
        ...(hasFAC ?
          [
            {
              text: "Coder Productivity Summary",
              url: "/reports/coder-productivity-summary",
              data: reportData,
            },
          ]
        : []),
      ],
    });
  }

  return (
    <Menu key={menuKey} linkRender={linkRender} items={menuList} onSelect={onSelect} openOnClick />
  );
};

const mapStateToProps = (state: State) => ({
  systemPreferences: state.systemPreferences,
});

export default connect(mapStateToProps)(MainMenuList);
